import { CaSelect } from "./CaSelect";

const CODES = `AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW`.split(/\s+/);
const DN = new Intl.DisplayNames(["es"], { type: "region" });
export const COUNTRIES = CODES.map((c) => ({ value: c, label: DN.of(c) || c })).sort((a, b) => a.label.localeCompare(b.label, "es"));
const norm = (s) => (s || "").toLocaleLowerCase("es").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
export const countryName = (code) => COUNTRIES.find((c) => c.value === code)?.label || code;
const codeOf = (name) => COUNTRIES.find((c) => norm(c.label) === norm(name))?.value;

export const ONLINE_LABEL = "Sin límite geográfico / Online";
export const SCOPE_OPTIONS = [
  { value: "spain", label: "España" },
  { value: "country", label: "Otro país" },
  { value: "multiple", label: "Varios países" },
  { value: "online", label: ONLINE_LABEL },
];

// Normaliza mercado (nuevo o heredado) a { scope, countries }. Nunca devuelve "Otro país"/"Varios países" como mercado.
export const readMarket = (b) => {
  const scope = b.market_scope || "";
  const countries = b.market_countries || [];
  if (scope) return { scope, countries };
  const m = (b.market || "").trim();
  if (!m) return { scope: "", countries: [] };
  if (norm(m) === norm(ONLINE_LABEL) || /online|sin limite/.test(norm(m))) return { scope: "online", countries: [] };
  const codes = m.split(/[,;/]| y /).map((x) => codeOf(x)).filter(Boolean);
  if (codes.length === 1 && codes[0] === "ES") return { scope: "spain", countries: ["ES"] };
  if (codes.length === 1) return { scope: "country", countries: codes };
  if (codes.length > 1) return { scope: "multiple", countries: codes };
  return { scope: "", countries: [], legacy: m };
};

// Representación persistida: market (texto retrocompatible) + market_scope + market_countries (ISO).
export const marketPatch = (scope, countries) => {
  // v15: al cambiar de mercado se vacía la ciudad/zona (podría no pertenecer al nuevo país).
  if (scope === "spain") return { market_scope: "spain", market_countries: ["ES"], market: "España", market_city: "" };
  if (scope === "online") return { market_scope: "online", market_countries: [], market: ONLINE_LABEL, market_city: "" };
  if (scope === "country" || scope === "multiple") {
    const list = scope === "country" ? countries.slice(0, 1) : countries;
    return { market_scope: scope, market_countries: list, market: list.map(countryName).join(", "), market_city: "" };
  }
  return { market_scope: "", market_countries: [], market: "", market_city: "" };
};

// v15 · Ciudad o zona del negocio y hasta dónde buscar.
export const REACH_OPTIONS = [
  { value: "solo_zona", label: "Solo mi zona" },
  { value: "zona_primero", label: "Mi zona primero y, si no hay suficientes, el resto del país" },
];
export const defaultReach = (biz) => (biz.market_reach || (biz.business_type === "local" ? "solo_zona" : "zona_primero"));
// Ciudad sugerida a partir de lo que ya escribió en el cuestionario («Ruzafa, Valencia» -> «Valencia»).
export const suggestedCity = (biz) => {
  const q = biz.questionnaire || {};
  const raw = (q.address_area || q.territory || q.delivery_area || "").trim();
  if (!raw) return "";
  const parts = raw.split(/[,;(]/).map((x) => x.replace(/[)]/g, "").trim()).filter(Boolean);
  const last = parts[parts.length - 1] || "";
  if (/^(españa|spain)$/i.test(last) && parts.length > 1) return parts[parts.length - 2];
  return last.split(/\s+y\s+/)[0].slice(0, 40);
};

export const marketSummary = (b) => {
  const { scope, countries, legacy } = readMarket(b);
  if (legacy) return legacy;
  const zone = (b.market_city || "").trim();
  const reach = zone ? ` (${defaultReach(b) === "solo_zona" ? "solo esa zona" : "esa zona primero"})` : "";
  if (scope === "spain") return zone ? `${zone}, España${reach}` : "España";
  if (scope === "online") return ONLINE_LABEL;
  if (scope === "country" && countries.length) return zone ? `${zone}, ${countryName(countries[0])}${reach}` : `Otro país: ${countryName(countries[0])}`;
  if (scope === "multiple" && countries.length) return `Varios países: ${countries.map(countryName).join(", ")}`;
  return "Sin indicar";
};

export const MarketField = ({ biz, onPatch, error, testPrefix = "onb" }) => {
  const { scope, countries, legacy } = readMarket(biz);
  return (
    <div className="ca-market">
      <CaSelect testId={`${testPrefix}-market-scope`} options={SCOPE_OPTIONS} value={scope} placeholder="Selecciona una opción…"
        onChange={(sc) => onPatch(marketPatch(sc, []))} />
      {legacy && <p className="ca-market-help" style={{ marginTop: 8 }}>Valor actual: «{legacy}». Elige una opción para estructurarlo.</p>}
      {scope === "country" && (
        <div className="field">
          <label>Selecciona el país</label>
          <CaSelect testId={`${testPrefix}-market-country`} options={COUNTRIES} value={countries[0] || ""} searchable invalid={!!error}
            placeholder="Busca o selecciona un país…" searchPlaceholder="Buscar país…" onChange={(c) => onPatch(marketPatch("country", [c]))} />
          {error && <span className="ca-field-error" role="alert">{error}</span>}
        </div>
      )}
      {(scope === "spain" || (scope === "country" && countries[0])) && (
        <ZoneFields biz={biz} onPatch={onPatch} testPrefix={testPrefix} />
      )}
      {scope === "multiple" && (
        <div className="field">
          <label>Selecciona los países</label>
          <CaSelect testId={`${testPrefix}-market-countries`} options={COUNTRIES} value={countries} multiple searchable invalid={!!error}
            placeholder="Selecciona los países" searchPlaceholder="Buscar país…"
            countLabel={(n) => `${n} ${n === 1 ? "país seleccionado" : "países seleccionados"}`}
            onChange={(list) => onPatch(marketPatch("multiple", list))} />
          {error && <span className="ca-field-error" role="alert">{error}</span>}
        </div>
      )}
    </div>
  );
};

function ZoneFields({ biz, onPatch, testPrefix }) {
  const city = biz.market_city || "";
  const sug = suggestedCity(biz);
  return (
    <div className="field" data-testid={`${testPrefix}-market-zone`}>
      <label htmlFor={`${testPrefix}-market-city`}>Ciudad o zona <span style={{ fontWeight: 400, opacity: 0.7 }}>(opcional)</span></label>
      <input id={`${testPrefix}-market-city`} data-testid={`${testPrefix}-market-city`} type="text" maxLength={60}
        value={city} placeholder="Ej.: Valencia, Madrid, Costa del Sol"
        onChange={(e) => onPatch({ market_city: e.target.value })} />
      {!city && sug && (
        <button type="button" className="ca-market-help" style={{ marginTop: 6, textDecoration: "underline", background: "none", border: 0, padding: 0, cursor: "pointer" }}
          data-testid={`${testPrefix}-market-city-suggest`} onClick={() => onPatch({ market_city: sug })}>
          Usar «{sug}»
        </button>
      )}
      <p className="ca-market-help" style={{ marginTop: 6 }}>
        Pon una ciudad o una zona amplia, no un barrio: en Instagram casi nadie indica el barrio.
      </p>
      {city.trim() && (
        <div style={{ marginTop: 10 }}>
          <label>¿Hasta dónde buscamos?</label>
          <CaSelect testId={`${testPrefix}-market-reach`} options={REACH_OPTIONS} value={defaultReach(biz)}
            onChange={(r) => onPatch({ market_reach: r })} />
        </div>
      )}
    </div>
  );
}
